import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { Hero, LandingCta } from "@/components/landing/Hero";
import { LiveBlocks } from "@/components/landing/LiveBlocks";
import { MoreInside, LandingFooter } from "@/components/landing/MoreInside";

import { captureRefFromUrl } from "@/lib/referral";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/")({
  ssr: false,
  beforeLoad: async () => {
    // Use getSession() (pure localStorage read, no network, no token refresh)
    // rather than getUser() so the landing page never depends on an auth-server
    // round-trip. A logged-out visitor has no session → renders the landing.
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      throw redirect({ to: "/home" });
    }
  },
  head: () => ({
    meta: [
      { title: "TurkeyDrive — афиша и сообщество русскоязычных в Турции" },
      {
        name: "description",
        content:
          "Находи события в своём городе, размещай свои мероприятия, вступай в сообщества по интересам и знакомься. Всё в одном приложении.",
      },
      { property: "og:title", content: "TurkeyDrive — афиша и сообщество русскоязычных в Турции" },
      {
        property: "og:description",
        content:
          "Находи события в своём городе, размещай свои мероприятия, вступай в сообщества по интересам и знакомься. Всё в одном приложении.",
      },
      { name: "twitter:title", content: "TurkeyDrive — афиша и сообщество русскоязычных в Турции" },
      { name: "twitter:description", content: "Находи события в своём городе, размещай свои мероприятия, вступай в сообщества по интересам и знакомься. Всё в одном приложении." },
      { property: "og:site_name", content: "TurkeyDrive" },
      { property: "og:locale", content: "ru_RU" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/SlPHRDnqmuPUBfG94njA1Thk2tB3/social-images/social-1784016270578-A0B0406F-154E-489C-B73B-9C488FC9B153.webp" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "640" },
      { property: "og:image:alt", content: "TurkeyDrive — почувствуйте себя в Турции как дома" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/SlPHRDnqmuPUBfG94njA1Thk2tB3/social-images/social-1784016270578-A0B0406F-154E-489C-B73B-9C488FC9B153.webp" },
      { name: "twitter:image:alt", content: "TurkeyDrive — почувствуйте себя в Турции как дома" },
    ],
    links: [{ rel: "canonical", href: "https://turkey-drive.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TurkeyDrive",
          url: "https://turkey-drive.com",
          description: "TurkeyDrive — платформенное сообщество русскоязычных в Турции. Единая афиша событий по всей стране, тематические сообщества, умные знакомства, каталог экспертов, консьерж-сервис, карточка резидента у каждого, «Спроси своих», работа и подрядчики, барахолка, роли и драйвы.",
          logo: "https://turkey-drive.com/icons/app-icon-512.png",
          sameAs: ["https://www.instagram.com/turkey_drive/"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "TurkeyDrive",
          url: "https://turkey-drive.com",
          inLanguage: "ru",
        }),
      },
    ],
  }),
  component: Landing,
});

function Landing() {
  useEffect(() => {
    captureRefFromUrl();
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-white font-sans text-td-ink antialiased">
      <SiteHeader />
      <Hero />
      <LiveBlocks />
      <MoreInside />
      <section className="mx-auto flex max-w-[1200px] flex-col items-center px-4 pb-12 pt-4 text-center md:px-6 lg:pb-16">
        <h2 className="font-display text-[32px] font-bold leading-[1.08] text-td-dark lg:text-[48px]">
          Приходи и <span className="text-td-teal">находи своих</span>
        </h2>
        <div className="mt-6 w-full max-w-[360px]">
          <LandingCta />
        </div>
      </section>
      <LandingFooter />
      
    </div>
  );
}
